"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resetSequenceSchema = exports.generateSequenceNumberSchema = exports.updateSequenceDefinitionSchema = exports.createSequenceDefinitionSchema = exports.sequenceDefinitionShape = exports.sequenceYearFormatEnum = exports.sequenceResetPolicyEnum = void 0;
const zod_1 = require("zod");
/**
 * `DAILY` and `FINANCIAL_YEAR` were both real values in the database before
 * either was here — `DAILY` on every `QUEUE_TOKEN` row since that entity
 * type was built, `FINANCIAL_YEAR` added 2026-09-10 alongside the migration
 * that taught the generator to actually honor it (see that migration's own
 * note on why `YEARLY` alone was wrong for every GST-series document
 * number). Omitting either from this enum would have this schema silently
 * reject — or on `.partial()` updates, silently DROP — a value the database
 * has been storing all along, which is exactly the trap the Sequence
 * Generator admin page hit the moment it tried to edit a `QUEUE_TOKEN` row
 * through this contract for the first time.
 */
exports.sequenceResetPolicyEnum = zod_1.z.enum(['NEVER', 'YEARLY', 'MONTHLY', 'DAILY', 'FINANCIAL_YEAR']);
exports.sequenceYearFormatEnum = zod_1.z.enum(['YY', 'YYYY']);
exports.sequenceDefinitionShape = {
    entityType: zod_1.z.string().min(1).max(50),
    hospitalId: zod_1.z.string().uuid().optional(),
    branchId: zod_1.z.string().uuid().optional(),
    departmentId: zod_1.z.string().uuid().optional(),
    prefix: zod_1.z.string().max(20).optional(),
    suffix: zod_1.z.string().max(20).optional(),
    includeYear: zod_1.z.boolean().default(false),
    yearFormat: exports.sequenceYearFormatEnum.optional(),
    resetPolicy: exports.sequenceResetPolicyEnum.default('NEVER'),
    padLength: zod_1.z.number().int().min(1).max(12).default(6),
    /**
     * WHERE THE COUNTER STANDS — settable, not just readable.
     *
     * Absent from this shape until the Sequence Generator admin page
     * (2026-09-10): an administrator aligning this system's numbering with an
     * existing paper register, or correcting one that was seeded wrong, needs
     * to set the next number directly rather than generating and discarding
     * documents to walk a counter forward by hand.
     *
     * A `number`, not the `bigint` the column actually is — the wire format
     * every other integer field here already uses, and no real sequence gets
     * within reach of `Number.MAX_SAFE_INTEGER`. `SequenceService.update()`
     * converts it and, more importantly, REFUSES to move it backward: see that
     * method's own note on why a lowered counter is a live duplicate-number
     * risk, not a cosmetic edit.
     */
    currentValue: zod_1.z.number().int().min(0).optional(),
};
exports.createSequenceDefinitionSchema = zod_1.z.object(exports.sequenceDefinitionShape);
exports.updateSequenceDefinitionSchema = exports.createSequenceDefinitionSchema.partial();
exports.generateSequenceNumberSchema = zod_1.z.object({
    entityType: zod_1.z.string().min(1).max(50),
    hospitalId: zod_1.z.string().uuid().optional(),
    branchId: zod_1.z.string().uuid().optional(),
    departmentId: zod_1.z.string().uuid().optional(),
});
/**
 * `POST /sequences/:id/reset` — a deliberate, reasoned act, not a field
 * edit. See `SequenceService.resetCounter()`'s own note for why this is a
 * separate endpoint from `PATCH /sequences/:id` rather than one more
 * optional field on that body: it is the only path that may lower
 * `currentValue`, and a mandatory `reason` is what an auditor reads back
 * later to understand why a series restarted.
 */
exports.resetSequenceSchema = zod_1.z.object({
    toValue: zod_1.z.number().int().min(0).default(0),
    reason: zod_1.z.string().trim().min(1).max(2000),
});
//# sourceMappingURL=sequence.schema.js.map